import React, { useEffect, useState } from "react";
import axios from "axios";
import { useNavigate } from "react-router-dom";
import { ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import {
  FaSearch,
  FaCalendar,
  FaClock,
  FaEye,
  FaUser,
  FaUsers,
  FaMoneyBillWave,
  FaMapMarkerAlt,
  FaDownload,
  FaFilter,
  FaImage,
  FaVideo,
  FaTag,
  FaGlobe,
  FaBuilding,
  FaCheckCircle,
  FaTimesCircle,
  FaSpinner,
  FaArrowLeft,
  FaArrowRight,
  FaPlay,
  FaPause,
  FaEdit,
  FaHourglassStart
} from "react-icons/fa";

const FreePendingAds = () => {
  const [ads, setAds] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [previewMedia, setPreviewMedia] = useState(null);
  const [currentPage, setCurrentPage] = useState(1);
  const rowsPerPage = 10;
  const navigate = useNavigate();
  const [refetchTrigger] = useState(false);

  // 🆕 Modal state for showing user details
  const [showModal, setShowModal] = useState(false);
  const [selectedAd, setSelectedAd] = useState(null);
  const [allUsers, setAllUsers] = useState({});
  const [loadingUsers, setLoadingUsers] = useState(false);

  useEffect(() => {
    const fetchAds = async () => {
      setLoading(true);
      setError(null);
      try {
        const response = await axios.get(
          `${process.env.REACT_APP_API_BASE_URL}/admin/get/pending/free/ad`,
          {
            headers: {
              Authorization: `Bearer ${localStorage.getItem("token")}`,
            },
          }
        );
        console.log("Fetched Ads Data:", response.data);
        // Ensure ads is always an array
        setAds(Array.isArray(response.data.data) ? response.data.data : []);
        console.log("Ads State Updated:", response.data.data);
      } catch (err) {
        setError("Failed to fetch ads");
      } finally {
        setLoading(false);
      }
    };
    fetchAds();
  }, [refetchTrigger]);

  const openMediaPreview = (mediaType, url) => {
    setPreviewMedia({
      type: mediaType,
      url: url
    });
  };

  // 🆕 Fetch targeted users for a specific ad
  const fetchAdUsers = async (adId, forceRefresh = false) => {
    try {
      setLoadingUsers(true);
      const timestamp = forceRefresh ? `?t=${Date.now()}` : '';

      const response = await axios.get(
        `${process.env.REACT_APP_API_BASE_URL}/admin/get/free/ad/${adId}/targeted-users${timestamp}`,
        {
          headers: {
            Authorization: `Bearer ${localStorage.getItem("token")}`,
          },
        }
      );

      console.log(`📊 Free Ad Users API Response:`, response.data);

      if (response.data.success) {
        console.log(`📊 Fetched users for free ad ${adId}:`, response.data.data);
        setAllUsers(prev => ({
          ...prev,
          [adId]: response.data.data
        }));
      } else {
        console.error(`❌ API returned success: false for free ad ${adId}:`, response.data);
      }
    } catch (error) {
      console.error(`❌ Error fetching free ad targeted users for ${adId}:`, error);
      if (error.response) {
        console.error(`❌ Error response:`, error.response.data);
        console.error(`❌ Error status:`, error.response.status);
      }
    } finally {
      setLoadingUsers(false);
    }
  };

  // 🆕 View ad details with user list
  const handleViewDetails = async (ad) => {
    console.log(`🔍 Opening free ad details for: ${ad._id}`);
    setSelectedAd(ad);
    setShowModal(true);

    // Fetch complete ad details with user information
    try {
      console.log(`🔍 Fetching complete free ad details for: ${ad._id}`);
      const response = await axios.get(
        `${process.env.REACT_APP_API_BASE_URL}/admin/get/free/ad/${ad._id}/targeted-users`,
        {
          headers: {
            Authorization: `Bearer ${localStorage.getItem("token")}`,
          },
        }
      );

      console.log(`📊 Free ad details API response:`, response.data);

      if (response.data.success) {
        setSelectedAd(response.data.data);
        setAllUsers(prev => ({
          ...prev,
          [ad._id]: response.data.data
        }));
      } else {
        console.error(`❌ API returned success: false:`, response.data);
      }
    } catch (error) {
      console.error(`❌ Error fetching free ad details:`, error);
      if (error.response) {
        console.error(`❌ Error response:`, error.response.data);
        console.error(`❌ Error status:`, error.response.status);
      }
    }
  };

  // 🆕 Handle modify ad
  const handleModifyAd = (ad) => {
    console.log(`🔧 Opening modify page for free ad: ${ad._id}`);
    navigate("/admin/free-ads/modify", {
      state: {
        adId: ad._id,
        adData: ad
      }
    });
  };

  const closeMediaPreview = () => {
    setPreviewMedia(null);
  };

  if (loading) return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 to-blue-50 flex items-center justify-center">
      <div className="flex flex-col items-center space-y-4">
        <div className="relative w-16 h-16">
          <div className="absolute inset-0 rounded-full border-4 border-transparent border-t-blue-500 animate-spin"></div>
          <div className="absolute inset-0 rounded-full border-4 border-transparent border-r-purple-500 animate-spin" style={{ animationDirection: 'reverse', animationDuration: '1.5s' }}></div>
          <div className="absolute inset-0 rounded-full border-4 border-transparent border-b-indigo-500 animate-spin" style={{ animationDuration: '2s' }}></div>
        </div>
        <p className="text-lg font-semibold text-gray-700">Loading Free Pending Ads...</p>
        <p className="text-sm text-gray-500">Please wait while we fetch the data</p>
      </div>
    </div>
  );

  if (error) return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 to-blue-50 flex items-center justify-center">
      <div className="bg-white rounded-2xl shadow-xl p-8 max-w-md mx-auto text-center border border-red-200">
        <div className="w-16 h-16 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-4">
          <FaTimesCircle className="text-red-500 text-2xl" />
        </div>
        <h3 className="text-xl font-bold text-gray-900 mb-2">Error Loading Data</h3>
        <p className="text-red-600 mb-4">{error}</p>
        <button
          onClick={() => window.location.reload()}
          className="bg-red-500 hover:bg-red-600 text-white px-6 py-3 rounded-xl font-semibold transition-colors"
        >
          Try Again
        </button>
      </div>
    </div>
  );

  // Pagination calculations
  const totalPages = Math.ceil(ads.length / rowsPerPage);
  const paginatedAds = [...ads].reverse().slice((currentPage - 1) * rowsPerPage, currentPage * rowsPerPage);

  const handlePageChange = (page) => {
    if (page >= 1 && page <= totalPages) {
      setCurrentPage(page);
    }
  };

  return (
    <div className="min-h-screen bg-slate-50/50 pb-12">
      <ToastContainer position="top-center" autoClose={3000} theme="dark" />

      {/* Premium Header */}
      <div className="max-w-7xl mx-auto px-2 sm:px-4 py-4 sm:py-8">
        <div className="bg-gradient-to-br from-amber-500 via-orange-600 to-rose-700 rounded-[2.5rem] shadow-2xl p-6 sm:p-14 text-white relative overflow-hidden">
          <div className="absolute top-0 right-0 w-80 h-80 bg-white/10 rounded-full blur-3xl -mr-40 -mt-40" />
          <div className="absolute bottom-0 left-0 w-64 h-64 bg-orange-400/10 rounded-full blur-2xl -ml-32 -mb-32" />

          <div className="relative z-10 flex flex-col lg:flex-row lg:items-center justify-between gap-8">
            <div className="flex items-center gap-4 sm:gap-8">
              <div className="p-4 sm:p-6 bg-white/15 rounded-[2rem] backdrop-blur-md shadow-inner border border-white/10">
                <FaClock className="text-2xl sm:text-5xl text-yellow-200 animate-pulse" />
              </div>
              <div>
                <h1 className="text-2xl sm:text-5xl font-black tracking-tight leading-none uppercase">Pending Ads</h1>
                <p className="text-orange-100 mt-2 text-[10px] sm:text-sm font-black uppercase tracking-[0.2em] opacity-80">Moderation Queue • Review & Approve</p>
              </div>
            </div>

            <div className="flex flex-wrap gap-2 sm:gap-4">
              {[
                { label: 'Running', path: '/admin/freerunningads', icon: FaPlay, color: 'emerald' },
                { label: 'Completed', path: '/admin/freecompletedads', icon: FaCheckCircle, color: 'blue' }
              ].map((btn) => (
                <button
                  key={btn.label}
                  onClick={() => navigate(btn.path)}
                  className="bg-white/10 hover:bg-white/20 border border-white/20 px-4 sm:px-6 py-3 rounded-2xl transition-all font-black text-[10px] sm:text-xs uppercase tracking-widest backdrop-blur-sm flex items-center gap-2 active:scale-95"
                >
                  <btn.icon /> {btn.label}
                </button>
              ))}
            </div>
          </div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-2 sm:px-4">
        {/* Content Section */}
        <div className="bg-white rounded-[2rem] sm:rounded-[3rem] shadow-2xl shadow-slate-200/50 border border-slate-100 overflow-hidden">

          {/* Desktop Table */}
          <div className="hidden lg:block overflow-x-auto">
            <table className="min-w-full divide-y divide-slate-100">
              <thead className="bg-slate-50/50">
                <tr>
                  <th className="px-8 py-6 text-left text-[10px] font-black text-slate-400 uppercase tracking-[0.2em]">Media</th>
                  <th className="px-8 py-6 text-left text-[10px] font-black text-slate-400 uppercase tracking-[0.2em]">Identification</th>
                  <th className="px-8 py-6 text-left text-[10px] font-black text-slate-400 uppercase tracking-[0.2em]">Category</th>
                  <th className="px-8 py-6 text-left text-[10px] font-black text-slate-400 uppercase tracking-[0.2em]">Geo-Target</th>
                  <th className="px-8 py-6 text-left text-[10px] font-black text-slate-400 uppercase tracking-[0.2em]">Timeline</th>
                  <th className="px-8 py-6 text-left text-[10px] font-black text-slate-400 uppercase tracking-[0.2em]">Status</th>
                  <th className="px-8 py-6 text-right text-[10px] font-black text-slate-400 uppercase tracking-[0.2em]">Actions</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-50">
                {paginatedAds.length === 0 ? (
                  <tr>
                    <td colSpan="7" className="px-8 py-20 text-center">
                      <FaHourglassStart className="text-5xl text-slate-200 mx-auto mb-4" />
                      <p className="font-black text-slate-400 uppercase tracking-widest">Queue is currently empty</p>
                    </td>
                  </tr>
                ) : (
                  paginatedAds.map((ad) => (
                    <tr key={ad._id} className="hover:bg-slate-50/50 transition-colors group">
                      <td className="px-8 py-5">
                        <div
                          className="relative w-16 h-16 rounded-2xl overflow-hidden cursor-pointer shadow-md group-hover:scale-110 transition-transform duration-500"
                          onClick={() => openMediaPreview(ad.mediaType?.toLowerCase() === 'video' ? 'video' : 'image', ad.mediaType?.toLowerCase() === 'video' ? ad.videoUrl : ad.imageUrl)}
                        >
                          {ad.mediaType?.toLowerCase() === 'video' ? (
                            <video src={ad.videoUrl} className="w-full h-full object-cover" />
                          ) : (
                            <img src={ad.imageUrl} className="w-full h-full object-cover" alt="Ad" />
                          )}
                          <div className="absolute inset-0 bg-slate-900/40 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity">
                            <FaEye className="text-white" />
                          </div>
                        </div>
                      </td>
                      <td className="px-8 py-5">
                        <p className="font-black text-slate-900 text-sm tracking-tight truncate max-w-[120px]">{ad._id}</p>
                        <p className="text-[10px] font-bold text-slate-400 uppercase">{new Date(ad.createdAt).toLocaleDateString()}</p>
                      </td>
                      <td className="px-8 py-5">
                        <span className="bg-slate-100 text-slate-600 px-3 py-1 rounded-xl text-[10px] font-black uppercase tracking-widest">{ad.adType}</span>
                      </td>
                      <td className="px-8 py-5">
                        <div className="flex flex-col">
                          <span className="text-xs font-black text-slate-800 uppercase">{ad.state || "National"}</span>
                          <span className="text-[10px] font-bold text-slate-400 truncate max-w-[150px]">{ad.city || "All Cities"}</span>
                        </div>
                      </td>
                      <td className="px-8 py-5">
                        <div className="flex items-center gap-2 text-slate-500">
                          <FaCalendar className="text-[10px]" />
                          <span className="text-xs font-bold">{new Date(ad.createdAt).toLocaleDateString()}</span>
                        </div>
                      </td>
                      <td className="px-8 py-5">
                        <span className={`px-3 py-1 rounded-xl text-[9px] font-black uppercase border ${ad.status === 'modified' ? 'bg-indigo-50 text-indigo-600 border-indigo-100' : 'bg-amber-50 text-amber-600 border-amber-100'}`}>
                          {ad.status}
                        </span>
                      </td>
                      <td className="px-8 py-5">
                        <div className="flex items-center justify-end gap-2">
                          <button
                            onClick={() => handleViewDetails(ad)}
                            className="p-3 bg-blue-50 text-blue-600 rounded-2xl hover:bg-blue-600 hover:text-white transition-all shadow-sm"
                            title="Quick View"
                          >
                            <FaEye />
                          </button>
                          <button
                            onClick={() => handleModifyAd(ad)}
                            className="p-3 bg-emerald-50 text-emerald-600 rounded-2xl hover:bg-emerald-600 hover:text-white transition-all shadow-sm"
                            title="Modify Campaign"
                          >
                            <FaEdit />
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>

          {/* Mobile Card View */}
          <div className="lg:hidden divide-y divide-slate-50">
            {paginatedAds.length === 0 ? (
              <div className="p-16 text-center">
                <FaHourglassStart className="text-5xl text-slate-200 mx-auto mb-4" />
                <p className="font-black text-slate-400 uppercase tracking-widest text-xs">Queue is empty</p>
              </div>
            ) : (
              paginatedAds.map((ad) => (
                <div key={ad._id} className="p-5 flex flex-col gap-5">
                  <div className="flex items-start justify-between">
                    <div className="flex items-center gap-4">
                      <div
                        className="relative w-16 h-16 rounded-2xl overflow-hidden shadow-xl border border-slate-100 shrink-0"
                        onClick={() => openMediaPreview(ad.mediaType?.toLowerCase() === 'video' ? 'video' : 'image', ad.mediaType?.toLowerCase() === 'video' ? ad.videoUrl : ad.imageUrl)}
                      >
                        {ad.mediaType?.toLowerCase() === 'video' ? (
                          <video src={ad.videoUrl} className="w-full h-full object-cover" />
                        ) : (
                          <img src={ad.imageUrl} className="w-full h-full object-cover" alt="Ad" />
                        )}
                        <div className="absolute inset-0 bg-slate-900/20 flex items-center justify-center">
                          <FaEye className="text-white text-xs" />
                        </div>
                      </div>
                      <div className="min-w-0">
                        <p className="font-black text-slate-900 text-sm truncate uppercase">{ad.adType}</p>
                        <p className="text-[10px] font-bold text-slate-400 font-mono tracking-tighter truncate">{ad._id}</p>
                        <div className="mt-1 flex items-center gap-2">
                          <span className={`px-2 py-0.5 rounded-lg text-[9px] font-black uppercase border ${ad.userType === 'influencer' ? 'bg-purple-100 text-purple-700 border-purple-200' : 'bg-blue-100 text-blue-700 border-blue-200'}`}>
                            {ad.userType}
                          </span>
                          <span className="text-[9px] font-black text-amber-500 uppercase tracking-widest">{ad.status}</span>
                        </div>
                      </div>
                    </div>
                    <div className="flex gap-2">
                      <button onClick={() => handleViewDetails(ad)} className="p-3 bg-blue-50 text-blue-600 rounded-2xl active:scale-90 transition-transform">
                        <FaEye />
                      </button>
                      <button onClick={() => handleModifyAd(ad)} className="p-3 bg-emerald-50 text-emerald-600 rounded-2xl active:scale-90 transition-transform">
                        <FaEdit />
                      </button>
                    </div>
                  </div>

                  <div className="bg-slate-50 rounded-[1.5rem] p-4 flex flex-col gap-4">
                    <p className="text-xs font-bold text-slate-600 line-clamp-2 leading-relaxed">
                      {ad.description}
                    </p>
                    <div className="flex items-center justify-between border-t border-slate-200/50 pt-3">
                      <div className="flex items-center gap-2">
                        <FaMapMarkerAlt className="text-rose-400 text-[10px]" />
                        <span className="text-[10px] font-black text-slate-700 capitalize tracking-tight">{ad.state || "National"} • {ad.city || "All cities"}</span>
                      </div>
                      <span className="text-[9px] font-black text-slate-400 uppercase">{new Date(ad.createdAt).toLocaleDateString()}</span>
                    </div>
                  </div>
                </div>
              ))
            )}
          </div>
        </div>

        {/* Premium Pagination */}
        {totalPages > 1 && (
          <div className="flex justify-center mt-12 mb-8">
            <nav className="inline-flex items-center gap-2 bg-white rounded-[2rem] shadow-2xl p-3 border border-slate-100">
              <button
                onClick={() => handlePageChange(currentPage - 1)}
                disabled={currentPage === 1}
                className="w-12 h-12 flex items-center justify-center rounded-2xl bg-slate-50 text-slate-400 disabled:opacity-50 hover:bg-amber-50 hover:text-amber-600 transition-all active:scale-90"
              >
                <FaArrowLeft />
              </button>
              <div className="flex gap-1.5">
                {Array.from({ length: Math.min(5, totalPages) }, (_, i) => {
                  const pNum = i + 1;
                  return (
                    <button
                      key={pNum}
                      onClick={() => handlePageChange(pNum)}
                      className={`w-12 h-12 rounded-2xl font-black text-sm transition-all active:scale-90 ${currentPage === pNum ? 'bg-gradient-to-br from-amber-500 to-orange-600 text-white shadow-xl shadow-orange-100' : 'bg-white text-slate-400 hover:bg-slate-50'}`}
                    >
                      {pNum}
                    </button>
                  );
                })}
              </div>
              <button
                onClick={() => handlePageChange(currentPage + 1)}
                disabled={currentPage === totalPages}
                className="w-12 h-12 flex items-center justify-center rounded-2xl bg-slate-50 text-slate-400 disabled:opacity-50 hover:bg-amber-50 hover:text-amber-600 transition-all active:scale-90"
              >
                <FaArrowRight />
              </button>
            </nav>
          </div>
        )}
      </div>

      {/* Premium Media Preview */}
      {previewMedia && (
        <div className="fixed inset-0 bg-slate-900/95 backdrop-blur-xl flex items-center justify-center z-[100] p-4 sm:p-8" onClick={closeMediaPreview}>
          <div className="relative max-w-5xl w-full flex flex-col items-center gap-6" onClick={e => e.stopPropagation()}>
            <button
              className="absolute -top-16 right-0 sm:-right-16 w-12 h-12 bg-white/10 hover:bg-rose-500 text-white rounded-[1.5rem] flex items-center justify-center text-2xl transition-all backdrop-blur-md"
              onClick={closeMediaPreview}
            >
              &times;
            </button>
            <div className="w-full bg-slate-800 rounded-[2.5rem] p-2 sm:p-4 shadow-2xl border border-white/5 overflow-hidden">
              {previewMedia.type === 'video' ? (
                <video src={previewMedia.url} className="max-h-[75vh] w-full rounded-[1.5rem] shadow-inner" controls autoPlay />
              ) : (
                <img src={previewMedia.url} alt="Preview" className="max-h-[75vh] w-full rounded-[1.5rem] object-contain shadow-inner" />
              )}
            </div>
            <p className="text-slate-400 font-black tracking-[0.3em] uppercase text-xs">Media Inspection View</p>
          </div>
        </div>
      )}

      {/* 🆕 User Details Modal */}
      {showModal && selectedAd && (
        <div className="fixed inset-0 bg-slate-900/80 backdrop-blur-md flex items-center justify-center z-[110] p-4 sm:p-8" onClick={() => setShowModal(false)}>
          <div className="max-w-6xl w-full max-h-[90vh] bg-white rounded-[3rem] shadow-3xl overflow-hidden flex flex-col" onClick={e => e.stopPropagation()}>
            {/* Modal Header */}
            <div className="p-8 sm:p-12 bg-gradient-to-br from-indigo-600 to-violet-800 text-white flex items-center justify-between shrink-0">
              <div>
                <h2 className="text-2xl sm:text-4xl font-black uppercase tracking-tight flex items-center gap-4">
                  <FaEye className="text-indigo-200" /> Campaign Intel
                </h2>
                <p className="text-indigo-100 text-xs sm:text-sm font-bold uppercase tracking-widest mt-2 opacity-70">AD-ID: {selectedAd._id}</p>
              </div>
              <button
                onClick={() => setShowModal(false)}
                className="w-12 h-12 bg-white/10 hover:bg-white/20 rounded-2xl flex items-center justify-center transition-all text-2xl"
              >
                &times;
              </button>
            </div>

            {/* Modal Scrollable Body */}
            <div className="flex-1 overflow-y-auto p-6 sm:p-12 custom-scrollbar bg-slate-50/50">
              {loadingUsers ? (
                <div className="py-20 flex flex-col items-center gap-6">
                  <FaSpinner className="text-5xl text-indigo-500 animate-spin" />
                  <p className="font-black text-slate-400 uppercase tracking-widest text-sm">Synchronizing Data...</p>
                </div>
              ) : (
                <div className="space-y-12">
                  {/* Campaign Specs Grid */}
                  <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                    <div className="bg-white p-6 rounded-[2rem] border border-slate-100 shadow-sm">
                      <p className="text-[10px] font-black text-slate-400 uppercase tracking-widest mb-2">Category</p>
                      <p className="text-lg font-black text-slate-800 uppercase tracking-tight">{selectedAd.adType}</p>
                    </div>
                    <div className="bg-white p-6 rounded-[2rem] border border-slate-100 shadow-sm">
                      <p className="text-[10px] font-black text-slate-400 uppercase tracking-widest mb-2">User Target</p>
                      <span className={`px-3 py-1 rounded-xl text-[10px] font-black uppercase ${selectedAd.userType === 'influencer' ? 'bg-purple-100 text-purple-700' : 'bg-blue-100 text-blue-700'}`}>
                        {selectedAd.userType}
                      </span>
                    </div>
                    <div className="bg-white p-6 rounded-[2rem] border border-slate-100 shadow-sm">
                      <p className="text-[10px] font-black text-slate-400 uppercase tracking-widest mb-2">Required Count</p>
                      <p className="text-lg font-black text-slate-800">{selectedAd.requiredReportersCount || 0} <span className="text-slate-400 text-xs uppercase ml-1">Submissions</span></p>
                    </div>
                  </div>

                  {/* Description Box */}
                  <div className="bg-white p-8 rounded-[2.5rem] border border-slate-100 shadow-sm">
                    <p className="text-[10px] font-black text-slate-400 uppercase tracking-widest mb-4">Campaign Mandate</p>
                    <p className="text-slate-700 font-bold leading-relaxed">{selectedAd.description}</p>
                  </div>

                  {/* Target Users Table */}
                  <div>
                    <div className="flex items-center gap-3 mb-6">
                      <FaUsers className="text-indigo-500 text-lg" />
                      <h3 className="text-lg font-black text-slate-800 uppercase tracking-tight">Deployment Roster</h3>
                    </div>

                    <div className="bg-white rounded-[2rem] border border-slate-100 shadow-sm overflow-hidden">
                      <div className="overflow-x-auto">
                        <table className="min-w-full divide-y divide-slate-100">
                          <thead className="bg-slate-50/50">
                            <tr>
                              <th className="px-6 py-4 text-left text-[9px] font-black text-slate-400 uppercase tracking-widest">Operator</th>
                              <th className="px-6 py-4 text-left text-[9px] font-black text-slate-400 uppercase tracking-widest">Role</th>
                              <th className="px-6 py-4 text-left text-[9px] font-black text-slate-400 uppercase tracking-widest">Deployment</th>
                              <th className="px-6 py-4 text-left text-[9px] font-black text-slate-400 uppercase tracking-widest">Verification</th>
                            </tr>
                          </thead>
                          <tbody className="divide-y divide-slate-50">
                            {allUsers[selectedAd._id]?.users?.length > 0 ? (
                              allUsers[selectedAd._id].users.map((user, idx) => (
                                <tr key={idx} className="hover:bg-slate-50 transition-colors">
                                  <td className="px-6 py-4 whitespace-nowrap">
                                    <div className="flex items-center gap-3">
                                      <div className="w-10 h-10 bg-slate-100 rounded-xl flex items-center justify-center font-black text-slate-400">
                                        {user.name?.charAt(0)}
                                      </div>
                                      <div>
                                        <p className="text-sm font-black text-slate-800 tracking-tight">{user.name}</p>
                                        <p className="text-[10px] font-bold text-slate-400">{user.email}</p>
                                      </div>
                                    </div>
                                  </td>
                                  <td className="px-6 py-4">
                                    <span className={`px-2 py-0.5 rounded-lg text-[9px] font-black uppercase tracking-widest ${user.role?.toLowerCase() === 'influencer' ? 'bg-purple-100 text-purple-600' : 'bg-blue-100 text-blue-600'}`}>
                                      {user.role}
                                    </span>
                                  </td>
                                  <td className="px-6 py-4">
                                    <p className="text-xs font-bold text-slate-800 tracking-tight">{user.city || 'National'}</p>
                                    <p className="text-[9px] font-bold text-slate-400 uppercase">{user.state || 'N/A'}</p>
                                  </td>
                                  <td className="px-6 py-4 text-right">
                                    <span className={`px-2 py-0.5 rounded-lg text-[9px] font-black uppercase tracking-widest ${user.status === 'completed' ? 'bg-emerald-100 text-emerald-600' : 'bg-amber-100 text-amber-600'}`}>
                                      {user.status || 'Active'}
                                    </span>
                                  </td>
                                </tr>
                              ))
                            ) : (
                              <tr>
                                <td colSpan="4" className="px-6 py-12 text-center">
                                  <p className="text-xs font-black text-slate-400 uppercase tracking-widest">No active deployments</p>
                                </td>
                              </tr>
                            )}
                          </tbody>
                        </table>
                      </div>
                    </div>
                  </div>
                </div>
              )}
            </div>

            {/* Modal Footer */}
            <div className="p-8 bg-slate-50 border-t border-slate-100 flex justify-end shrink-0">
              <button
                onClick={() => setShowModal(false)}
                className="px-8 py-3 bg-slate-900 text-white rounded-2xl font-black text-[10px] uppercase tracking-[0.2em] shadow-xl hover:bg-slate-800 transition-all active:scale-95"
              >
                Close Intel View
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default FreePendingAds;
